import { i18n } from '@lingui/core'
import { t, Trans } from '@lingui/macro'
import { Box, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { bilingualDateFormat } from 'app/views/common/Formats'
import { FieldArray } from 'formik'
import { useSelector } from 'react-redux'
import { myI18n } from 'translation/I18nConnectedProvider'
import { objectiveTranslations } from '../objectives/FormObjectives'
import './FormObjectivesUpdate.css'
import { getObjectivesUpdateFieldsLengthLimits, lastReportTitle, previousReportTitle } from './FormObjectivesUpdateUtils'

/**
 * Form element which allows adding and configuring project objectives updates.
 * Objective Updates are saved as Objective_Update__c child objects on connected Grantee Report object.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {SalesforceObjectConnection} connectedObject Connected Salesforce object of type Opportunity.
 * @param  {Object} connectedMap Map of all connected objects.
 * @param  {string[]} typeProps.options Array of phases defined in the form editor. For each one user will need to fill the required information.
 * @param  {number}  [typeProps.maxChar] If provided, the number of characters that a text input field will be restricted to.
 * @param  {number}  [typeProps.maxWords] If provided, the number of words that a text input field will be restricted to.
 * @param  {number}  [typeProps.rowQuantity = 1] Number of rows which a text input field should occupy.
 * @param  {boolean} [typeProps.expandable=false] If a text field should expand further below if new line is inserted.
 */
export const FormObjectivesUpdate = ({
  id,
  useMultiuser,
  muBag,
  title,
  helpText,
  connectedObject,
  editMode,
  disabled,
  typeProps,
  describeMap = {},
  langVersion,
  i18n,
  connectedMap,
  ...props
}) => {
  const language = useSelector((state) => state.user.language) || 'en'

  const {
    identifiedNeedChangesLimit,
    desiredOutcomeChangesLimit,
    actualOutcomesLimit
  } = getObjectivesUpdateFieldsLengthLimits(describeMap, typeProps)

  const granteeReportConnectedId = typeProps.connectedTo?.find(
    (item) =>
      connectedMap?.[item?.connectedObject]?.objectType ===
      'FGM_Base__Grantee_Report__c'
  )?.connectedObject

  const latestReport = connectedObject?.latestReport
  const latestObjectiveUpdates =
    latestReport?.objectiveUpdates || []

  const invalid = Boolean(
    !connectedObject ||
      !connectedObject.Id ||
      connectedObject.attributes.type !== 'Opportunity'
  )
  if (invalid && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        <Trans>
          There is no object connected in editor or main connected object is not
          of "Opportunity" type!
        </Trans>
      </div>
    )
  }

  if (!granteeReportConnectedId && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        <Trans>FORM_OBJECTIVES_UPDATE_GRANTEE_REPORT_CONNECTION_ERROR</Trans>
      </div>
    )
  }

  return (
    <div className='form-objectives-update-root'>
      {/* <div className='description-container'>
        <Typography className='description-text'>
          <Trans>FORM_OBJECTIVES_UPDATE_TOP_INFO_LABEL</Trans>
        </Typography>
      </div> */}
      <h2 className='main-title'>{title}</h2>
      <FieldArray
        name={id}
        render={({ push, form, name, remove }) => {
          const objectives = form.values[name] || []
          const alreadySelected = {}
          objectives.forEach((obj, oIndex) => {
            if (obj.objective) {
              if (alreadySelected[obj.objective]) {
                alreadySelected[obj.objective].push(oIndex)
              } else {
                alreadySelected[obj.objective] = [oIndex]
              }
            }
          })

          return (
            <Box
              sx={{ borderColor: 'primary.main' }}
              className='form-objectives-update body'
            >
              <Box
                sx={{ borderColor: 'primary.main' }}
                className='top-labels section header-border'
              >
                <Grid item>
                  <Typography className='text-header'>
                    <Trans>FORM_OBJECTIVES_UPDATE_VIEW_ONLY_TITLE</Trans>
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography className='text-header text-header-margin-left'>
                    <Trans>FORM_OBJECTIVES_UPDATE_UPDATES_TITLE</Trans>
                  </Typography>
                </Grid>
              </Box>

              {objectives.map((item, index) => {
                const isLast = index === objectives.length - 1

                return (
                  <Box key={index} className={isLast ? '' : 'section-border'}>
                    <div className='section section-border'>
                      <Typography className='text-header'>
                        {myI18n._(item.objective)}
                      </Typography>
                    </div>
                    <div className='section needs-outcomes-section section-border'>
                      <Grid item className='section-name-column'>
                        <Typography className='text-name'>
                          <Trans>Needs</Trans>
                        </Typography>
                      </Grid>

                      <div className='outcomes-field-columns'>
                        <Grid item container>
                          <Box className='needs-outcomes-text-container'>
                            {item.needs}
                          </Box>

                          <Grid item container>
                            <div className='answers'>
                              {latestObjectiveUpdates
                                .filter(
                                  (answer) =>
                                    answer.identifiedNeedChanges &&
                                    answer.objectiveId === item.id
                                )
                                .map((answer, idx) => {
                                  const title =
                                    idx === 0
                                      ? lastReportTitle
                                      : previousReportTitle
                                  const formattedDate = bilingualDateFormat(
                                    latestReport.submissionDate,
                                    language
                                  )
                                  return (
                                    <Grid
                                      key={idx}
                                      direction='column'
                                      className='answer'
                                    >
                                      <Typography className='text-report'>
                                        {title}: {formattedDate}
                                      </Typography>
                                      <Typography>
                                        {answer.identifiedNeedChanges}
                                      </Typography>
                                    </Grid>
                                  )
                                })}
                            </div>
                          </Grid>
                        </Grid>

                        <Grid item xs>
                          <TextFieldWithFormik
                            className='exclude-from-global-styles'
                            useMultiuser={useMultiuser}
                            muBag={muBag}
                            name={
                              id +
                              `[${index}].objectivesUpdate.identifiedNeedChanges`
                            }
                            topLabel={
                              <Trans>
                                FORM_OBJECTIVES_UPDATE_IDENTIFIED_NEEDS_CHANGES
                              </Trans>
                            }
                            multiline
                            minRows={5}
                            maxRows={5}
                            disabled={disabled}
                            margin='normal'
                            limit={
                              identifiedNeedChangesLimit &&
                              Number(identifiedNeedChangesLimit)
                            }
                          />
                        </Grid>
                      </div>
                    </div>

                    <div className='section needs-outcomes-section'>
                      <Grid item className='section-name-column'>
                        <Typography className='text-name'>
                          <Trans>Outcomes</Trans>
                        </Typography>
                      </Grid>

                      <div className='outcomes-field-columns'>
                        <Grid item container>
                          <Box className='needs-outcomes-text-container outcomes'>
                            {item.outcomes.map((outcome, idx) => {
                              return (
                                <div key={idx}>
                                  <Typography className='text'>
                                    {idx + 1}. {outcome}
                                  </Typography>
                                </div>
                              )
                            })}
                          </Box>
                          <Grid item container>
                            <div className='answers'>
                              {latestObjectiveUpdates
                                .filter(
                                  (answer) =>
                                    answer.desiredOutcomeChanges &&
                                    answer.objectiveId === item.id
                                )
                                .map((answer, idx) => {
                                  const title =
                                    idx === 0
                                      ? lastReportTitle
                                      : previousReportTitle
                                  const formattedDate = bilingualDateFormat(
                                    latestReport.submissionDate,
                                    language
                                  )
                                  return (
                                    <Grid
                                      key={idx}
                                      direction='column'
                                      className='answer'
                                    >
                                      <Typography className='text-report'>
                                        {title}: {formattedDate}
                                      </Typography>
                                      <Typography>
                                        {answer.desiredOutcomeChanges}
                                      </Typography>
                                    </Grid>
                                  )
                                })}
                            </div>
                          </Grid>
                        </Grid>

                        <Grid item xs>
                          <TextFieldWithFormik
                            className='exclude-from-global-styles'
                            fullWidth
                            useMultiuser={useMultiuser}
                            muBag={muBag}
                            name={
                              id +
                              `[${index}].objectivesUpdate.desiredOutcomeChanges`
                            }
                            topLabel={
                              <Trans>
                                FORM_OBJECTIVES_UPDATE_DESIRED_OUTCOME_CHANGES
                              </Trans>
                            }
                            multiline
                            minRows={5}
                            maxRows={5}
                            disabled={disabled}
                            margin='normal'
                            limit={
                              desiredOutcomeChangesLimit &&
                              Number(desiredOutcomeChangesLimit)
                            }
                          />
                        </Grid>
                      </div>
                    </div>

                    <div className='section needs-outcomes-section divider-container'>
                      <div className='section-name-column' />
                      <div className='divider' />
                    </div>

                    <div className='needs-outcomes-section section'>
                      <div className='section-name-column' />
                      <div className='outcomes-field-columns'>
                        <Grid item container xs className='answers'>
                          {latestObjectiveUpdates
                            .filter(
                              (answer) =>
                                answer.actualOutcomes &&
                                answer.objectiveId === item.id
                            )
                            .map((answer, idx) => {
                              const title =
                                idx === 0
                                  ? lastReportTitle
                                  : previousReportTitle
                              const formattedDate = bilingualDateFormat(
                                latestReport.submissionDate,
                                language
                              )

                              return (
                                <Grid
                                  key={idx}
                                  direction='column'
                                  className='answer'
                                >
                                  <Typography className='text-report'>
                                    {title}: {formattedDate}
                                  </Typography>
                                  <Typography>
                                    {answer.actualOutcomes}
                                  </Typography>
                                </Grid>
                              )
                            })}
                        </Grid>

                        <Grid item xs>
                          <TextFieldWithFormik
                            className='exclude-from-global-styles'
                            useMultiuser={useMultiuser}
                            required
                            fullWidth
                            muBag={muBag}
                            name={
                              id + `[${index}].objectivesUpdate.actualOutcomes`
                            }
                            topLabel={
                              <Trans>
                                FORM_OBJECTIVES_UPDATE_ACTUAL_OUTCOMES
                              </Trans>
                            }
                            multiline
                            minRows={5}
                            maxRows={5}
                            disabled={disabled}
                            margin='normal'
                            limit={
                              actualOutcomesLimit && Number(actualOutcomesLimit)
                            }
                          />
                        </Grid>
                      </div>
                    </div>
                  </Box>
                )
              })}
            </Box>
          )
        }}
      />
    </div>
  )
}

export const formObjectivesUpdateValueToExportText = (value, _) => {
  if (!value) {
    return ''
  }

  return value.map((item, index) => {
    const { objective, objectivesUpdate } = item
    const { 
      desiredOutcomeChanges, 
      actualOutcomes, 
      identifiedNeedChanges 
    } = objectivesUpdate

    const objectiveTranslation = objectiveTranslations[objective]
    const objectiveString = objectiveTranslation ? i18n._(objectiveTranslation) : objective || ''

    return [
      `${index + 1}. ${objectiveString}`,
      identifiedNeedChanges ? `${i18n._(t`FORM_OBJECTIVES_UPDATE_IDENTIFIED_NEEDS_CHANGES`)}:\n${identifiedNeedChanges}` : '',
      desiredOutcomeChanges ? `${i18n._(t`FORM_OBJECTIVES_UPDATE_DESIRED_OUTCOME_CHANGES`)}:\n${desiredOutcomeChanges}` : '',
      actualOutcomes ? `${i18n._(t`Actual outcome`)}:\n${actualOutcomes}` : ''
    ].filter(Boolean).join('\n')

  }).join('\n\n')
}